/**
 * CustomPainelDarAcesso — achar uma ação e entregá-la a quem precisa.
 *
 * SEÇÃO PRÓPRIA, e não um pedaço da consulta. Ela nasceu escondida no fim de
 * "Quem pode o quê", aparecendo só depois de dois cliques certos — quem não
 * soubesse que estava lá não achava, e ele não achou. Decisão dele, 19/08/2026:
 * *"vamos separar que eu acho que vai ficar melhor mesmo"*.
 *
 * A diferença que justifica duas seções: uma RESPONDE (quem alcança isto?) e a
 * outra AGE (dá isto a alguém). Misturadas, a tela tinha dois propósitos e não
 * anunciava nenhum.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { api, ApiError } from '@/lib/api';
import { CustomBuscaAcao, acoesDoCatalogo } from './CustomBuscaAcao';
import type { Modulo } from './CustomBuscaAcao';

export interface PerfilPacote {
  id: number;
  codigo: string;
  nome: string;
  usuarios: number;
  admin: boolean;
  permissoes: string[];
}

export interface UsuarioLinha {
  id: number;
  nome: string;
  perfil_id: number | null;
  perfil_nome: string | null;
  admin: boolean;
  status: 'ativo' | 'inativo';
  excecoes?: { mais: number; menos: number };
}

export interface QuemPode {
  id: number;
  nome: string;
  via: 'admin' | 'perfil' | 'excecao';
  perfil?: string;
  quem?: string | null;
  motivo?: string | null;
}

interface Props {
  catalogo: Modulo[];
  usuarios: UsuarioLinha[];
  perfis: PerfilPacote[];
  soAdmin: string[];
  aoSalvar: () => void;
  /** A barra de "tem coisa por salvar" — a mesma da tela toda. */
  BarraSalvar: React.ComponentType<{
    mudancas: string[];
    salvando: boolean;
    aoSalvar: () => void;
    aoDescartar: () => void;
  }>;
}

export function CustomPainelDarAcesso({
  catalogo,
  usuarios,
  perfis,
  soAdmin,
  aoSalvar,
  BarraSalvar,
}: Props) {
  const [permissao, setPermissao] = useState<string | null>(null);
  const [quemAlcanca, setQuemAlcanca] = useState<QuemPode[] | null>(null);
  const [perfisRascunho, setPerfisRascunho] = useState<Set<number>>(new Set());
  const [pessoasRascunho, setPessoasRascunho] = useState<Record<number, boolean | null>>({});
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);

  function escolher(nova: string | null) {
    setPermissao(nova);
    setQuemAlcanca(null);
    setPerfisRascunho(
      new Set(
        perfis.filter((p) => !p.admin && p.permissoes.includes(nova ?? '')).map((p) => p.id),
      ),
    );
    setPessoasRascunho({});
    setMotivo('');
  }

  useEffect(() => {
    if (!permissao) return;
    api
      .get<{ pessoas: QuemPode[] }>(`/configuracoes/acessos/quem-pode/${permissao}`)
      .then((r) => setQuemAlcanca(r.pessoas))
      .catch(() => setQuemAlcanca([]));
  }, [permissao]);

  const bloqueada = !!permissao && soAdmin.includes(permissao);

  /**
   * Como a pessoa está HOJE:
   *   true = exceção concedida · false = exceção revogada · null = segue o perfil
   */
  function viaAtual(usuarioId: number): boolean | null {
    const achado = (quemAlcanca ?? []).find((p) => p.id === usuarioId);
    if (achado) return achado.via === 'excecao' ? true : null;
    const u = usuarios.find((x) => x.id === usuarioId);
    const perfilDava = perfis.some(
      (p) => p.id === u?.perfil_id && p.permissoes.includes(permissao ?? ''),
    );
    return perfilDava ? false : null;
  }

  const perfisAntes = new Set(
    perfis.filter((p) => !p.admin && p.permissoes.includes(permissao ?? '')).map((p) => p.id),
  );
  const nomeDoPerfil = (id: number) => perfis.find((p) => p.id === id)?.nome ?? '?';
  const quantasPessoas = (id: number) => perfis.find((p) => p.id === id)?.usuarios ?? 0;

  // Cada mudança dita com o ALCANCE dela: mexer num perfil não muda uma pessoa,
  // muda todas as dele — e é essa a diferença que faz alguém clicar sem querer.
  const mudancas: string[] = [];
  for (const p of perfis.filter((x) => !x.admin)) {
    const antes = perfisAntes.has(p.id);
    const depois = perfisRascunho.has(p.id);
    if (antes === depois) continue;
    const n = quantasPessoas(p.id);
    const quantos = n === 1 ? '1 pessoa' : `${n} pessoas`;
    mudancas.push(
      depois
        ? `Perfil ${nomeDoPerfil(p.id)}: passa a dar esta ação — vale para ${quantos}`
        : `Perfil ${nomeDoPerfil(p.id)}: deixa de dar esta ação — ${quantos} perdem`,
    );
  }
  for (const [idTexto, decisao] of Object.entries(pessoasRascunho)) {
    const id = Number(idTexto);
    if (decisao === viaAtual(id)) continue;
    const nome = usuarios.find((u) => u.id === id)?.nome ?? '?';
    if (decisao === true) mudancas.push(`${nome}: passa a ter, como extra só dela`);
    else if (decisao === false) mudancas.push(`${nome}: deixa de ter, retirado só dela`);
    else mudancas.push(`${nome}: volta a seguir o perfil`);
  }

  async function salvar() {
    setSalvando(true);
    try {
      const r = await api.put<{ pessoas: QuemPode[] }>(
        `/configuracoes/acessos/quem-pode/${permissao}`,
        { perfis: [...perfisRascunho], pessoas: pessoasRascunho, motivo: motivo.trim() || null },
      );
      setQuemAlcanca(r.pessoas);
      setPessoasRascunho({});
      setMotivo('');
      toast.success('Acesso atualizado.');
      aoSalvar();
    } catch (e: unknown) {
      toast.error(e instanceof ApiError ? e.message : 'Não consegui salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <>
      <h5 className="mb-2">Dar acesso</h5>
      <p className="text-secondary">
        Procure a ação e entregue-a a quem precisa. Pelo <strong>perfil</strong>,
        vale para todas as pessoas dele de uma vez; por <strong>pessoa</strong>, é
        exceção — só dela.
      </p>

      <CustomBuscaAcao
        catalogo={catalogo}
        escolhida={permissao}
        aoEscolher={escolher}
        focoInicial
        placeholder="Que ação você quer dar? ambiente, execução, despesa…"
        vazio={
          <p className="text-secondary small">
            Comece digitando — <em>ambientes</em>, <em>execução</em>, <em>despesa</em>,{' '}
            <em>viagem</em>. São {acoesDoCatalogo(catalogo).length} ações no total.
          </p>
        }
      />

      {permissao && bloqueada && (
        <p className="text-secondary">
          <i className="bi bi-lock-fill me-1"></i>
          Esta ação é de administrador, por código — não se distribui por aqui.
        </p>
      )}

      {permissao && !bloqueada && (
        <>
          <BarraSalvar
            mudancas={mudancas}
            salvando={salvando}
            aoSalvar={salvar}
            aoDescartar={() => escolher(permissao)}
          />

          {mudancas.length > 0 && (
            <div className="input-group input-group-sm rounded mb-3">
              <span className="input-group-text text-theme-1">
                <i className="bi bi-chat-left-text"></i>
              </span>
              <input
                type="text"
                className="form-control"
                maxLength={255}
                placeholder="Motivo (opcional) — fica no registro de quem deu"
                value={motivo}
                onChange={(e) => setMotivo(e.target.value)}
              />
            </div>
          )}

          <div className="small text-uppercase text-secondary mb-1">Perfis</div>
          <ul className="list-group list-group-flush bg-none mb-3">
            {perfis
              .filter((p) => !p.admin)
              .map((p) => (
                <li className="list-group-item" key={p.id}>
                  <div className="row gx-3 align-items-center">
                    <div className="col">
                      <span className="fw-medium">{p.nome}</span>
                      <span className="d-block small text-secondary">
                        {p.usuarios} pessoa{p.usuarios === 1 ? '' : 's'}
                      </span>
                    </div>
                    <div className="col-auto">
                      <div className="form-check form-switch">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          role="switch"
                          id={`da-perfil-${p.id}`}
                          checked={perfisRascunho.has(p.id)}
                          onChange={() =>
                            setPerfisRascunho((atual) => {
                              const proximo = new Set(atual);
                              if (proximo.has(p.id)) proximo.delete(p.id);
                              else proximo.add(p.id);
                              return proximo;
                            })
                          }
                        />
                        <label className="form-check-label" htmlFor={`da-perfil-${p.id}`}></label>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
          </ul>

          <div className="small text-uppercase text-secondary mb-1">Pessoas</div>
          <ul className="list-group list-group-flush bg-none">
            {usuarios
              .filter((u) => !u.admin)
              .map((u) => {
                const doPerfil = perfisRascunho.has(u.perfil_id ?? -1);
                const escolha = u.id in pessoasRascunho ? pessoasRascunho[u.id] : undefined;
                const temAgora = escolha === undefined ? viaAtual(u.id) : escolha;
                const efetivo = temAgora === null ? doPerfil : !!temAgora;
                return (
                  <li className="list-group-item" key={u.id}>
                    <div className="row gx-3 align-items-center">
                      <div className="col">
                        <span className="fw-medium">{u.nome}</span>
                        <span className="d-block small text-secondary">
                          {efetivo && temAgora === true && !doPerfil && 'extra só dela'}
                          {efetivo &&
                            (temAgora === null || doPerfil) &&
                            `vem do perfil ${u.perfil_nome ?? '—'}`}
                          {!efetivo && temAgora === false && doPerfil && 'retirado só dela'}
                          {!efetivo && !doPerfil && `o perfil ${u.perfil_nome ?? '—'} não dá`}
                        </span>
                      </div>
                      <div className="col-auto">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            id={`da-pessoa-${u.id}`}
                            checked={efetivo}
                            onChange={() =>
                              setPessoasRascunho((atual) => {
                                // Voltar a coincidir com o perfil APAGA a exceção,
                                // em vez de gravar redundância.
                                const querAgora = !efetivo;
                                const proximo = { ...atual };
                                if (querAgora === doPerfil) proximo[u.id] = null;
                                else proximo[u.id] = querAgora;
                                return proximo;
                              })
                            }
                          />
                          <label className="form-check-label" htmlFor={`da-pessoa-${u.id}`}></label>
                        </div>
                      </div>
                    </div>
                  </li>
                );
              })}
          </ul>
        </>
      )}
    </>
  );
}
